import { useEffect, useState } from 'react';
import { api } from '../../api';
import type { GmMob, MobResumo } from '../../api/types';
import { idiomaAtual, t } from '../../i18n/idioma';
import { nomeDoInimigo } from '../../i18n/nomes';
import { formatarHora, minutoAgora } from '../../world/relogio';
import { BotaoPerigoso, type Rodar } from './comum';

interface Props {
  busy: boolean;
  rodar: Rodar;
  mobs: readonly GmMob[];
  catalogo: readonly MobResumo[];
  targetMobId: number | null;
  invocando: boolean;
  onInvocar(defId: string, alcance: number | undefined): void;
  onCancelarInvocar(): void;
  onRemovido(shortId: number): void;
}

const fase = (m: number) => {
  const h = m / 60;
  return h < 5 || h >= 20 ? 'noite' : h < 7 ? 'amanhecer' : h < 18 ? 'dia' : 'entardecer';
};

/** Mundo: a hora de Britannia (escolhe na faixa, aplica com confirmação) e os inimigos invocados. */
export function MundoGm({ busy, rodar, mobs, catalogo, targetMobId, invocando, onInvocar, onCancelarInvocar, onRemovido }: Props) {
  const idioma = idiomaAtual();
  const [servidor, setServidor] = useState(() => Math.floor(minutoAgora()));
  const [escolhida, setEscolhida] = useState(() => Math.floor(minutoAgora() / 10) * 10);
  const [tipo, setTipo] = useState('');
  const [alcance, setAlcance] = useState('8');

  useEffect(() => {
    const id = window.setInterval(() => setServidor(Math.floor(minutoAgora())), 5000);
    return () => window.clearInterval(id);
  }, []);
  useEffect(() => {
    setTipo(cur => (catalogo.some(m => m.defId === cur) ? cur : catalogo[0]?.defId ?? ''));
  }, [catalogo]);

  const aplicarHora = () => {
    const antes = Math.floor(minutoAgora());
    const h = Math.floor(escolhida / 60) % 24, m = escolhida % 60;
    void rodar(async () => { await api.gmHora(h, m); setServidor(escolhida); }, {
      cat: t('gm.x.cat.mundo'),
      txt: t('gm.x.reg.hora', { hora: formatarHora(escolhida, idioma) }),
      desfazer: () => api.gmHora(Math.floor(antes / 60) % 24, antes % 60)
    });
  };

  const remover = (mob: GmMob) => void rodar(async () => { await api.gmRemoveMob(mob.shortId); onRemovido(mob.shortId); }, {
    cat: t('gm.x.cat.mundo'), txt: t('gm.x.reg.removeu', { nome: nomeDoInimigo(mob.defId, mob.name) })
  });

  const removerTodos = () => void rodar(async () => {
    for (const mob of mobs) {
      await api.gmRemoveMob(mob.shortId);
      onRemovido(mob.shortId);
    }
  }, { cat: t('gm.x.cat.mundo'), txt: t('gm.x.reg.removeuTodos', { n: mobs.length }) });

  const alcanceNum = (() => {
    const v = Number(alcance.trim());
    return alcance.trim() === '' || !Number.isFinite(v) || v === 8 ? undefined : v;
  })();

  return (
    <>
      <div className="gmx-grade gmx-g2">
        <div className="gmx-card">
          <p className="gmx-h">{t('gm.hora.titulo')} <small>{t('gm.x.paraTodos')}</small></p>
          <div className="gmx-linha">
            <span className="gmx-hora">{formatarHora(escolhida, idioma)}</span>
            <span className="gmx-meta">{t(`gm.x.fase.${fase(escolhida)}`)} · {t('gm.x.agoraNoServidor', { hora: formatarHora(servidor, idioma) })}</span>
          </div>
          <div className="gmx-faixa" role="slider" aria-label={t('gm.hora.titulo')} aria-valuemin={0} aria-valuemax={1439} aria-valuenow={escolhida}
            tabIndex={0} title={t('gm.x.faixaDica')}
            onClick={e => {
              const r = e.currentTarget.getBoundingClientRect();
              setEscolhida((Math.round(((e.clientX - r.left) / r.width) * 144) * 10) % 1440);
            }}
            onKeyDown={e => {
              if (e.key === 'ArrowRight') { setEscolhida(m => (m + 10) % 1440); e.preventDefault(); }
              if (e.key === 'ArrowLeft') { setEscolhida(m => (m + 1430) % 1440); e.preventDefault(); }
            }}>
            <i style={{ left: `calc(${(escolhida / 1440) * 100}% - 1px)` }} />
            {[0, 6, 12, 18, 24].map(h => <span key={h} style={{ left: `${(h / 24) * 100}%` }}>{h}h</span>)}
          </div>
          <div className="gmx-linha">
            {([['amanhecer', 360], ['meioDia', 720], ['entardecer', 1140], ['meiaNoite', 0]] as const).map(([k, m]) => (
              <button key={k} type="button" className="btn btn--tiny" onClick={() => setEscolhida(m)}>{t(`gm.hora.${k}`)}</button>
            ))}
            <span className="gmx-esp" />
            <BotaoPerigoso rotulo={t('gm.x.aplicarNoServidor')} confirmar={t('gm.x.confirmarHora', { hora: formatarHora(escolhida, idioma) })}
              disabled={busy} onConfirm={aplicarHora} />
          </div>
          <p className="gmx-meta" style={{ marginTop: 6 }}>{t('gm.hora.regra')}</p>
        </div>

        <div className="gmx-card">
          <p className="gmx-h">{t('gm.secaoInimigos')}</p>
          <div className="gmx-linha">
            <select value={tipo} onChange={e => setTipo(e.target.value)} disabled={busy || invocando || catalogo.length === 0}
              aria-label={t('gm.tipoDeInimigo')} style={{ flex: 1 }}>
              {catalogo.map(m => <option key={m.defId} value={m.defId}>{nomeDoInimigo(m.defId, m.nome)}</option>)}
            </select>
            <label className="gmx-meta">{t('gm.alcance')}{' '}
              <input value={alcance} onChange={e => setAlcance(e.target.value.replace(/\D/g, ''))} style={{ width: 54 }}
                aria-label={t('gm.alcanceDoPasseio')} disabled={busy || invocando} />
            </label>
          </div>
          <div className="gmx-linha">
            {invocando
              ? <button type="button" className="btn" onClick={onCancelarInvocar}>{t('gm.cliqueNoMapa')} · {t('comum.cancelar')}</button>
              : <button type="button" className="btn btn--primary" disabled={busy || !tipo} onClick={() => onInvocar(tipo, alcanceNum)}>{t('gm.invocar')}</button>}
            <span className="gmx-meta">{t('gm.x.invocarDica')}</span>
          </div>
        </div>
      </div>

      <div className="gmx-card" style={{ marginTop: 10 }}>
        <div className="gmx-linha">
          <p className="gmx-h" style={{ margin: 0 }}>{t('gm.inimigosNestaCidade')} <small>{mobs.length}</small></p>
          <span className="gmx-esp" />
          {targetMobId !== null && (
            <button type="button" className="btn btn--tiny" disabled={busy} onClick={() => {
              const mob = mobs.find(m => m.shortId === targetMobId);
              if (mob) remover(mob);
              else void rodar(async () => { await api.gmRemoveMob(targetMobId); onRemovido(targetMobId); });
            }}>{t('gm.removerSelecionado')}</button>
          )}
          {mobs.length > 1 && (
            <BotaoPerigoso rotulo={t('gm.x.removerTodos')} confirmar={t('gm.x.confirmarRemoverTodos', { n: mobs.length })}
              disabled={busy} onConfirm={removerTodos} className="btn--tiny" />
          )}
        </div>
        <div className="gmx-mobs">
          {mobs.length === 0 && <p className="gmx-meta">{t('gm.nenhumInimigo')}</p>}
          {mobs.map(mob => (
            <div key={mob.shortId} className="gmx-mob" aria-current={mob.shortId === targetMobId}>
              <span><b>{nomeDoInimigo(mob.defId, mob.name)}</b> <span className="gmx-meta" style={{ display: 'inline' }}>
                · {mob.x}, {mob.y} · {t('gm.alcanceCurto', { n: mob.range })}</span></span>
              <button type="button" className="btn btn--tiny btn--perigo" disabled={busy} onClick={() => remover(mob)}>{t('comum.remover')}</button>
            </div>
          ))}
        </div>
      </div>
    </>
  );
}
